import { useMemo, useState } from 'react'
import { useQuery } from 'convex/react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import {
  Loader2,
  MousePointerClick,
  Clock,
  Search,
} from 'lucide-react'

function formatDuration(ms: number | null) {
  if (!ms) return '--'
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)
  if (h > 0) return `${h}h ${m % 60}m`
  if (m > 0) return `${m}m ${s % 60}s`
  return `${s}s`
}

function formatDate(ts: number) {
  const d = new Date(ts)
  const now = new Date()
  const diff = now.getTime() - ts
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  if (diff < 172_800_000) return 'yesterday'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function AdminSessionsPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const navigate = useNavigate()
  const [cursor, setCursor] = useState<number | undefined>(undefined)
  const [search, setSearch] = useState('')

  const data = useQuery(
    api.sessionRecordings.adminListRecordings,
    isAdmin ? { cursor } : 'skip'
  )

  const visibleRecordings = useMemo(() => {
    if (!data?.recordings) return undefined
    const term = search.trim().toLowerCase()
    if (!term) return data.recordings
    return data.recordings.filter((rec) => {
      const name = (rec.userName || '').toLowerCase()
      const email = (rec.userEmail || '').toLowerCase()
      return name.includes(term) || email.includes(term)
    })
  }, [data?.recordings, search])

  if (!isAdmin) {
    return (
      <div className="flex h-full items-center justify-center bg-neutral-950 text-neutral-400">
        Admin access required
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="mx-auto max-w-7xl px-6 py-6">
        <div className="mb-6 flex items-center gap-3">
          <h1 className="text-lg font-semibold tracking-tight">Session Recordings</h1>
          {data && (
            <span className="text-sm text-neutral-500">
              {visibleRecordings?.length ?? 0}
              {search ? ` of ${data.recordings.length}` : ''} sessions
            </span>
          )}
        </div>

        <div className="mb-3 relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by user name or email"
            className="w-full rounded-md border border-neutral-800 bg-neutral-900 py-2 pl-9 pr-3 text-sm text-neutral-200 placeholder:text-neutral-600 focus:border-neutral-700 focus:outline-none focus:ring-0"
          />
        </div>

        {!data ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-500" />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-neutral-800">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-800 bg-neutral-900/50 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Duration</th>
                    <th className="px-4 py-3 text-right">Events</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800/50">
                  {visibleRecordings?.map((rec) => (
                    <tr
                      key={rec._id}
                      onClick={() => navigate(`/admin/sessions/${rec._id}`)}
                      className="cursor-pointer transition-colors hover:bg-neutral-900/80"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          {rec.userAvatar ? (
                            <img
                              src={rec.userAvatar}
                              className="h-6 w-6 rounded-full"
                              alt=""
                            />
                          ) : (
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-800 text-[10px] font-medium text-neutral-400">
                              {(rec.userName || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-neutral-200">
                              {rec.userName}
                            </div>
                            {rec.userEmail && (
                              <div className="truncate text-xs text-neutral-500">
                                {rec.userEmail}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-sm text-neutral-300">
                          <Clock className="h-3 w-3 text-neutral-500" />
                          <span className="font-mono text-xs">
                            {formatDuration(rec.durationMs)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5 text-sm text-neutral-300">
                          <MousePointerClick className="h-3 w-3 text-neutral-500" />
                          <span className="font-mono text-xs">{rec.eventCount}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {rec.status === 'recording' ? (
                          <span className="flex items-center gap-1 text-xs text-emerald-400">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                            live
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-500">
                            {rec.status}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono text-xs text-neutral-500">
                          {formatDate(rec.startedAt)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data.nextCursor && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCursor(data.nextCursor!)}
                  className="border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800"
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
